import { useState } from "react";
import { Check, Loader2, MessageCircle } from "lucide-react";

const INDIA_STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh",
  "Jammu & Kashmir", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Other / Outside India"];

const field = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/15";
const label = "mb-1.5 block text-[11px] font-mono font-medium uppercase tracking-widest text-slate-500";

export function ExamChips({ taxonomy, value, onChange, compact }) {
  const toggle = (name) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name].slice(0, 12));
  const chip = (name, testid) => {
    const on = value.includes(name);
    return (
      <button key={name} type="button" data-testid={testid} onClick={() => toggle(name)} aria-pressed={on}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color,color,transform] active:scale-95 ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>
        {on && <Check className="h-3 w-3" />}{name}
      </button>
    );
  };
  const groupLabels = new Set(taxonomy.groups.map((g) => g.label));
  const pickedFine = value.filter((v) => !groupLabels.has(v));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" data-testid="exam-groups">
        {taxonomy.groups.map((g) => chip(g.label, `exam-group-${g.key.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`))}
        {pickedFine.map((name) => chip(name, `exam-picked-${name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`))}
      </div>
      {!compact && taxonomy.exams.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer select-none text-xs font-medium text-indigo-700 hover:underline" data-testid="exam-more-toggle">
            Pick specific exams ({taxonomy.exams.length})
          </summary>
          <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto pr-1" data-testid="exam-fine-list">
            {taxonomy.exams.filter((e) => !value.includes(e.name)).map((e) => chip(e.name, `exam-${e.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`))}
          </div>
        </details>
      )}
    </div>
  );
}

export function SubscribeForm({ taxonomy, initialExams, onSubmit, onStart, compact }) {
  const [f, setF] = useState({ name: "", email: "", whatsapp: "", class_level: "", state: "", whatsapp_consent: true, website: "" });
  const [exams, setExams] = useState(initialExams || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (exams.length === 0) return setErr("Pick at least one exam you're preparing for.");
    if (f.whatsapp.replace(/\D/g, "").length < 10) return setErr("Enter a valid 10-digit WhatsApp number.");
    setBusy(true);
    try { await onSubmit({ ...f, exams }); } catch (ex) { setErr(ex?.response?.data?.detail?.toString?.() || ex?.response?.data?.detail?.[0]?.msg || "Could not subscribe. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} onFocus={onStart} className="space-y-4" data-testid="subscribe-form" noValidate>
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div><label className={label} htmlFor="ez-name">Your name</label>
          <input id="ez-name" data-testid="sub-name" className={field} value={f.name} onChange={set("name")} placeholder="Aarav Sharma" required minLength={2} maxLength={80} autoComplete="name" /></div>
        <div><label className={label} htmlFor="ez-whatsapp">WhatsApp number</label>
          <div className="relative"><MessageCircle className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
            <input id="ez-whatsapp" data-testid="sub-whatsapp" className={`${field} pl-9`} value={f.whatsapp} onChange={set("whatsapp")} placeholder="98765 43210" inputMode="tel" autoComplete="tel" required /></div></div>
        <div className={compact ? "" : "sm:col-span-2"}><label className={label} htmlFor="ez-email">Email</label>
          <input id="ez-email" data-testid="sub-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" required /></div>
        <div><label className={label} htmlFor="ez-class">Class / stage</label>
          <select id="ez-class" data-testid="sub-class" className={field} value={f.class_level} onChange={set("class_level")}>
            <option value="">Select…</option>{taxonomy.class_levels.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div><label className={label} htmlFor="ez-state">State</label>
          <select id="ez-state" data-testid="sub-state" className={field} value={f.state} onChange={set("state")}>
            <option value="">Select…</option>{INDIA_STATES.map((s) => <option key={s}>{s}</option>)}</select></div>
      </div>
      <div>
        <div className={label}>Exams you're preparing for <span className="normal-case tracking-normal text-slate-400">· pick all that apply</span></div>
        <ExamChips taxonomy={taxonomy} value={exams} onChange={setExams} compact={compact} />
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <label className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-600">
        <input type="checkbox" data-testid="sub-consent" checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-slate-900" />
        <span>Send me exam date changes, admit-card and result alerts on WhatsApp. No spam — unsubscribe any time.</span>
      </label>
      {err && <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="sub-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="sub-submit"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60 sm:w-auto">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{busy ? "Subscribing…" : "Get my updates + free AI guidance"}
      </button>
    </form>
  );
}
